import type { BrowserElementFingerprint } from "./scripts"

export type BrowserPageMetrics = {
    url: string
    viewport: {
        width: number
        height: number
        scrollX: number
        scrollY: number
        devicePixelRatio: number
    }
}

type BrowserImageSize = { width: number; height: number }

export type BrowserScreenshotObservation = {
    id: string
    tabId: string
    navigationGeneration: number
    page: BrowserPageMetrics
    sourceImage: BrowserImageSize
    emittedImage: BrowserImageSize
    sourceDigest: string
}

export type BrowserCoordinateTarget = {
    x: number
    y: number
    url: string
    submit?: boolean
    submissionIntent?: "search" | "authentication" | "other"
    opaque?: boolean
    fingerprint?: BrowserElementFingerprint
}

export async function browserScreenshotDigest(dataUrl: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(dataUrl))
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")
}

export function browserScreenshotPointToViewport(observation: BrowserScreenshotObservation, point: { x: number; y: number }): { x: number; y: number } | null {
    if (
        !Number.isFinite(point.x) ||
        !Number.isFinite(point.y) ||
        point.x < 0 ||
        point.y < 0 ||
        point.x >= observation.emittedImage.width ||
        point.y >= observation.emittedImage.height ||
        observation.emittedImage.width <= 0 ||
        observation.emittedImage.height <= 0
    ) {
        return null
    }
    return {
        x: Math.round((point.x / observation.emittedImage.width) * observation.page.viewport.width),
        y: Math.round((point.y / observation.emittedImage.height) * observation.page.viewport.height),
    }
}

export function browserScreenshotStillCurrent(
    observation: BrowserScreenshotObservation,
    navigationGeneration: number,
    page: BrowserPageMetrics,
    sourceDigest: string
): boolean {
    return (
        observation.navigationGeneration === navigationGeneration &&
        observation.sourceDigest === sourceDigest &&
        observation.page.url === page.url &&
        observation.page.viewport.width === page.viewport.width &&
        observation.page.viewport.height === page.viewport.height &&
        observation.page.viewport.scrollX === page.viewport.scrollX &&
        observation.page.viewport.scrollY === page.viewport.scrollY &&
        observation.page.viewport.devicePixelRatio === page.viewport.devicePixelRatio
    )
}

export function browserCoordinateTargetStillCurrent(before: BrowserCoordinateTarget, after: BrowserCoordinateTarget): boolean {
    return (
        before.x === after.x &&
        before.y === after.y &&
        before.url === after.url &&
        Boolean(before.submit) === Boolean(after.submit) &&
        before.submissionIntent === after.submissionIntent &&
        Boolean(before.opaque) === Boolean(after.opaque) &&
        JSON.stringify(before.fingerprint) === JSON.stringify(after.fingerprint)
    )
}
